<script lang="ts">
	import { onDestroy } from 'svelte';
	import { fly, fade } from 'svelte/transition';
	import { format } from 'svelte-i18n';
	import { mobile } from '../../services/mobile-rework';
	import {
		DRAG_CLOSE_THRESHOLD,
		DRAG_EXIT_DELAY_MS,
		getExitY,
		getBackdropBlurPx,
		getBackdropBgIntensity,
		OUTRO_FLY_DURATION_MS,
		OUTRO_FLY_Y,
	} from './ai-modal-drag';
	import { applyScrollLock, clearScrollLock } from './ai-modal-scroll-lock';

	export let open = false;
	export let onClose: () => void = () => {};
	export let bordered = false;
	export let size: 'chat' | 'content' = 'chat';
	export let enableScrollLock = false;
	export let onTransitionDone: (() => void) | undefined;
	/** When scroll lock is applied, optional callback(scrollY) that returns a cleanup (e.g. interaction guards). */
	export let onScrollLockApply: ((lockedScrollY: number) => (() => void) | void) | undefined;
	/** When true, render the header slot; when false, render the default close button. */
	export let useHeaderSlot = false;
	/** Called on Escape before closing; return false to prevent close (e.g. close submenu first). */
	export let onBeforeClose: (() => boolean | void) | undefined;

	const isAndroid = mobile.OS === 'Android';

	let touchStartY = 0;
	let dragOffset = 0;
	let isDragging = false;
	let closedByDrag = false;
	let lockedScrollY = 0;
	let scrollLockCleanup: (() => void) | null = null;
	let didApplyScrollLock = false;
	let backButtonCleanup: (() => void) | null = null;

	$: if (open && mobile.isMobile) {
		if (!backButtonCleanup) {
			const handler = (e: Event) => {
				e.preventDefault();
				onClose();
			};
			mobile.addEventListener('back_button', handler);
			backButtonCleanup = () => {
				mobile.removeEventListener('back_button', handler);
				backButtonCleanup = null;
			};
		}
	} else {
		backButtonCleanup?.();
		backButtonCleanup = null;
	}

	$: if (open) {
		closedByDrag = false;
		dragOffset = 0;
	}

	$: if (open && enableScrollLock) {
		if (!didApplyScrollLock) {
			lockedScrollY = applyScrollLock();
			const cleanup = onScrollLockApply?.(lockedScrollY);
			scrollLockCleanup = typeof cleanup === 'function' ? cleanup : null;
			didApplyScrollLock = true;
		}
	} else if (didApplyScrollLock) {
		scrollLockCleanup?.();
		scrollLockCleanup = null;
		clearScrollLock(true, lockedScrollY);
		didApplyScrollLock = false;
	}

	$: backdropBlurPx = getBackdropBlurPx(dragOffset);
	$: backdropBgIntensity = getBackdropBgIntensity(dragOffset);

	function panelOutro(node: HTMLElement) {
		if (isAndroid) return { duration: 0 };
		if (closedByDrag) return fade(node, { duration: DRAG_EXIT_DELAY_MS });
		return fly(node, { y: OUTRO_FLY_Y, duration: OUTRO_FLY_DURATION_MS });
	}

	function handleClose() {
		onClose();
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (open && e.key === 'Escape') {
			if (onBeforeClose?.() === false) return;
			handleClose();
		}
	}

	function handleBackdropClick() {
		handleClose();
	}

	function handleTouchStart(e: TouchEvent) {
		touchStartY = e.touches[0].clientY;
		isDragging = true;
	}

	function handleTouchMove(e: TouchEvent) {
		if (!isDragging) return;
		if (e.cancelable) e.preventDefault();
		const currentY = e.touches[0].clientY;
		const delta = currentY - touchStartY;
		dragOffset = Math.max(0, delta);
	}

	function handleTouchEnd() {
		if (!isDragging) return;
		isDragging = false;
		if (dragOffset > DRAG_CLOSE_THRESHOLD) {
			if (isAndroid) {
				dragOffset = 0;
				handleClose();
			} else {
				closedByDrag = true;
				dragOffset = getExitY();
			}
		} else {
			dragOffset = 0;
		}
	}

	function handleTouchCancel() {
		if (!isDragging) return;
		handleTouchEnd();
		if (typeof document !== 'undefined') {
			document.dispatchEvent(new Event('touchend', { bubbles: true, cancelable: true }));
		}
	}

	const bindDragHandle = (node: HTMLElement) => {
		node.addEventListener('touchstart', handleTouchStart);
		node.addEventListener('touchmove', handleTouchMove, { passive: false });
		node.addEventListener('touchend', handleTouchEnd);
		node.addEventListener('touchcancel', handleTouchCancel);
		return {
			destroy() {
				node.removeEventListener('touchstart', handleTouchStart);
				node.removeEventListener('touchmove', handleTouchMove);
				node.removeEventListener('touchend', handleTouchEnd);
				node.removeEventListener('touchcancel', handleTouchCancel);
			},
		};
	};

	onDestroy(() => {
		backButtonCleanup?.();
		backButtonCleanup = null;
		if (didApplyScrollLock) {
			scrollLockCleanup?.();
			scrollLockCleanup = null;
			clearScrollLock(false, lockedScrollY);
			didApplyScrollLock = false;
		}
	});
</script>

<svelte:window on:keydown={handleKeyDown} />

{#if open}
	<div class="ai-scene-modal">
		<div
			class="modal-backdrop"
			class:backdrop-closing={closedByDrag && dragOffset > DRAG_CLOSE_THRESHOLD}
			style="--ai-chat-backdrop-blur: {backdropBlurPx}px; --ai-chat-backdrop-bg-intensity: {backdropBgIntensity}"
			on:click={handleBackdropClick}
			on:keydown={() => {}}
			role="presentation"
		></div>

		{#if !closedByDrag}
			<div
				class="modal-panel"
				class:modal-panel--bordered={bordered}
				class:modal-panel--content={size === 'content'}
				class:snap-back={!isDragging && dragOffset === 0}
				class:drag-exit={closedByDrag}
				style="--ai-chat-drag-offset: {dragOffset}px"
				in:fly={{ y: OUTRO_FLY_Y, duration: OUTRO_FLY_DURATION_MS }}
				on:introend={() => onTransitionDone?.()}
				out:panelOutro
				on:outroend={() => {
					if (closedByDrag) handleClose();
				}}
			>
				<div class="drag-handle" use:bindDragHandle>
					<span class="drag-handle-bar" aria-hidden="true"></span>
				</div>

				{#if useHeaderSlot}
					<slot name="header" />
				{:else}
					<button
						class="close-btn"
						on:click={handleClose}
						aria-label={$format('close', { default: 'Close' })}
					>
						<i class="fas fa-times"></i>
					</button>
				{/if}

				<div class="modal-body">
					<slot />
				</div>
			</div>
		{/if}
	</div>
{/if}

<style>
	.ai-scene-modal {
		position: fixed;
		inset: 0;
		z-index: 998;
		pointer-events: none;
	}

	.modal-backdrop {
		position: fixed;
		inset: 0;
		z-index: 999;
		pointer-events: auto;
		background: radial-gradient(
			120% 85% at 50% 0%,
			rgba(88, 133, 187, calc(0.22 * var(--ai-chat-backdrop-bg-intensity, 1))) 0%,
			rgba(7, 18, 38, calc(0.62 * var(--ai-chat-backdrop-bg-intensity, 1))) 52%,
			rgba(7, 18, 38, calc(0.78 * var(--ai-chat-backdrop-bg-intensity, 1))) 100%
		);
		backdrop-filter: blur(var(--ai-chat-backdrop-blur, 6px)) saturate(120%);
		opacity: 0;
		animation: ai-scene-modal-backdrop-fade-in 0.18s ease-out forwards;
	}

	@keyframes ai-scene-modal-backdrop-fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	.modal-backdrop.backdrop-closing {
		animation: none;
		opacity: 0;
		transition: opacity 0.28s ease;
	}

	.modal-panel {
		position: fixed;
		z-index: 1000;
		pointer-events: auto;
		display: flex;
		flex-direction: column;
		background: var(--color-background-2, #000000);
		overflow: visible;
		top: 50%;
		left: 50%;
		width: 680px;
		max-width: 90vw;
		height: 80vh;
		max-height: 800px;
		border-radius: 12px;
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
		transform: translate(-50%, -50%) translateY(var(--ai-chat-drag-offset, 0px));
		overflow: hidden;
	}

	.modal-panel.modal-panel--bordered {
		border: 1px solid rgba(255, 255, 255, 0.2);
	}

	.modal-panel.modal-panel--content {
		min-height: 740px;
		height: auto;
		max-height: 90vh;
		padding: 26px 0;
		align-items: center;
	}

	.modal-panel::before {
		content: '';
		position: absolute;
		top: -280px;
		left: 50%;
		width: 459px;
		height: 381px;
		margin-left: -229.5px;
		background: radial-gradient(41.53% 41.53% at 50% 53.97%, #bfe3f3 0%, #253871 100%);
		border-radius: 459px;
		filter: blur(200px);
		opacity: 1;
		pointer-events: none;
		z-index: 0;
	}

	.modal-panel.snap-back {
		transition: transform 0.2s ease;
	}

	.modal-panel.drag-exit {
		transition: transform 0.28s ease;
	}

	.drag-handle {
		display: none;
		flex-shrink: 0;
		touch-action: none;
	}

	.drag-handle-bar {
		display: block;
		width: 36px;
		height: 5px;
		background: var(--color-border, #444);
		border-radius: 3px;
	}

	.close-btn {
		position: absolute;
		top: 23px;
		right: 23px;
		width: 20px;
		height: 20px;
		padding: 0;
		background: transparent;
		border: none;
		color: var(--color-text-primary, #eaeaea);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2;
	}

	.close-btn:hover {
		opacity: 0.8;
	}

	.close-btn i {
		font-size: 16px;
	}

	.modal-body {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		overflow: visible;
		position: relative;
		z-index: 1;
	}

	.modal-panel--content .modal-body {
		overflow: hidden;
	}

	@media (max-width: 768px) {
		.modal-backdrop {
			top: var(--ios-visual-viewport-offset-top, 0px);
			right: 0;
			left: 0;
			bottom: auto;
			height: var(--ios-visual-viewport-height, 100%);
			background: radial-gradient(
				120% 85% at 50% 0%,
				rgba(88, 133, 187, calc(0.22 * var(--ai-chat-backdrop-bg-intensity, 1))) 0%,
				rgba(7, 18, 38, calc(0.62 * var(--ai-chat-backdrop-bg-intensity, 1))) 52%,
				rgba(7, 18, 38, calc(0.78 * var(--ai-chat-backdrop-bg-intensity, 1))) 100%
			);
			backdrop-filter: blur(var(--ai-chat-backdrop-blur, 6px)) saturate(120%);
		}

		.modal-panel {
			top: var(--ios-visual-viewport-offset-top, 0px);
			right: 0;
			left: 0;
			bottom: auto;
			width: 100%;
			height: var(--ios-visual-viewport-height, 100%);
			max-width: none;
			max-height: none;
			border-radius: 0;
			transform: translateY(var(--ai-chat-drag-offset, 0px));
		}

		.modal-panel.modal-panel--bordered {
			border: none;
		}

		.modal-panel.modal-panel--content {
			min-height: 100%;
			height: 100%;
			max-height: none;
			padding-top: 0;
		}

		.drag-handle {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			min-height: 44px;
			padding-top: env(safe-area-inset-top, 12px);
			padding-bottom: 12px;
			flex-shrink: 0;
			cursor: grab;
		}

		.drag-handle:active {
			cursor: grabbing;
		}

		.close-btn {
			display: none;
		}
	}
</style>
